
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';

interface RevenuePerBikeData {
  currentRevenue: number;
  previousRevenue: number;
  activebikes: number;
  trendPercentage: number;
  monthlyData: Array<{ month: string; revenue: number }>;
}

export const RevenuePerBikeCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RevenuePerBikeData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      
      // Получаем общую сумму платежей за текущий месяц
      const currentMonth = new Date();
      const firstDayCurrentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const firstDayPreviousMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
      const lastDayPreviousMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0);

      const [paymentsResponse, bikesResponse, previousPaymentsResponse] = await Promise.all([
        supabase
          .from('payments')
          .select('amount')
          .gte('payment_date', firstDayCurrentMonth.toISOString())
          .in('status', ['paid', 'completed']),
        
        supabase
          .from('bikes')
          .select('status')
          .in('status', ['available', 'in_use']),
        
        supabase
          .from('payments')
          .select('amount')
          .gte('payment_date', firstDayPreviousMonth.toISOString())
          .lt('payment_date', firstDayCurrentMonth.toISOString())
          .in('status', ['paid', 'completed'])
      ]);

      if (paymentsResponse.error) throw paymentsResponse.error;
      if (bikesResponse.error) throw bikesResponse.error;
      if (previousPaymentsResponse.error) throw previousPaymentsResponse.error;

      const currentTotal = paymentsResponse.data?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;
      const previousTotal = previousPaymentsResponse.data?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;
      const activebikes = bikesResponse.data?.length || 1;

      const currentRevenue = currentTotal / activebikes;
      const previousRevenue = previousTotal / activebikes;
      const trendPercentage = previousRevenue > 0 ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : 0;

      setData({
        currentRevenue,
        previousRevenue,
        activebikes,
        trendPercentage,
        monthlyData: [] // Заглушка для будущего расширения
      });

    } catch (err: any) {
      console.error('Ошибка при получении данных о доходе:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-lg">
            <DollarSign className="h-5 w-5 mr-2 text-green-500" />
            Доход на 1 велосипед
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-2 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-lg">
            <DollarSign className="h-5 w-5 mr-2 text-green-500" />
            Доход на 1 велосипед
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-red-500">
            <p className="text-sm">Ошибка загрузки данных</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isPositiveTrend = data.trendPercentage >= 0;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-lg">
          <DollarSign className="h-5 w-5 mr-2 text-green-500" />
          Доход на 1 велосипед
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="text-2xl font-bold text-green-600">
            {data.currentRevenue.toLocaleString('ru-RU', { 
              style: 'currency', 
              currency: 'RUB',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}
          </div>
          
          <div className="flex items-center space-x-2">
            {isPositiveTrend ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
            <span className={`text-sm font-medium ${isPositiveTrend ? 'text-green-600' : 'text-red-600'}`}>
              {Math.abs(data.trendPercentage).toFixed(1)}%
            </span>
            <span className="text-xs text-muted-foreground">
              vs прошлый месяц
            </span>
          </div>

          <div className="text-xs text-muted-foreground">
            Активных велосипедов: {data.activebikes}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
